import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
interface EmbeddedContextProps {
  inIframe: boolean;
  inWebview: boolean;
  isEmbedded: boolean;
  isReady: boolean;
  setIsReady: (ready: boolean) => void;
  sendMessage: (message: any) => void;
  wrapperOrigin: string | null;
  setWrapperOrigin: (origin: string | null) => void;
  // Whether the wrapper's UPDUO_AUTH handshake has confirmed the current
  // identity. Starts false; flips true after a successful UPDUO_AUTH round-trip
  // and back to false on UPDUO_LOGOUT. Only meaningful when isEmbedded — used to
  // gate rendering so the prior account never renders from a stale persisted
  // session while waiting for the handshake.
  embeddedAuthConfirmed: boolean;
  setEmbeddedAuthConfirmed: (v: boolean) => void;
}
export const EmbeddedContext = createContext<EmbeddedContextProps>({
  inIframe: false,
  inWebview: false,
  isEmbedded: false,
  isReady: false,
  setIsReady: () => {},
  sendMessage: () => {},
  wrapperOrigin: null,
  setWrapperOrigin: () => {},
  embeddedAuthConfirmed: false,
  setEmbeddedAuthConfirmed: () => {}
});
export const MPEmbeddedProvider: FC<PropsWithChildren> = ({
  children
}) => {
  // Detect embedding synchronously so the first render already has correct values.
  // These checks (window.parent, window.Upduo) are safe to call at init time.
  const [inIframe] = useState(() => typeof window !== 'undefined' && window.parent !== window);
  const [inWebview] = useState(() => typeof window !== 'undefined' && typeof (window as any).Upduo !== 'undefined');
  const isEmbedded = inIframe || inWebview;
  const [isReady, setIsReady] = useState(false);
  const [wrapperOrigin, setWrapperOrigin] = useState<string | null>(null);
  const [embeddedAuthConfirmed, setEmbeddedAuthConfirmed] = useState(false);

  // Track the latest wrapperOrigin in a ref so the stable message sender can
  // read it at call time without being re-created. MPEmbeddedListener sets
  // wrapperOrigin from an allowlisted origin during the auth handshake.
  const wrapperOriginRef = useRef<string | null>(wrapperOrigin);
  useEffect(() => {
    wrapperOriginRef.current = wrapperOrigin;
  }, [wrapperOrigin]);

  // Initialize the message sender synchronously so it's available before any
  // child useLayoutEffect fires. Previously this lived in a useEffect which
  // runs AFTER children's useLayoutEffect — causing MPEmbeddedListener's
  // UPDUO_READY to be silently dropped on first mount.
  //
  // Scope the postMessage target to the known wrapperOrigin so we never leak
  // messages to an unexpected parent frame. It's null only before the first
  // handshake message (UPDUO_READY), which carries no sensitive data; fall
  // back to '*' for that single case so the handshake can still bootstrap.
  const sendMessageRef = useRef<((message: any) => void) | null>(inWebview ? (msg: any) => (window as any).Upduo.postMessage(JSON.stringify(msg)) : inIframe ? (msg: any) => window.parent.postMessage(msg, wrapperOriginRef.current ?? '*') : null);

  // Stable function that reads from the ref at call time
  const sendMessage = useCallback((message: any) => {
    if (sendMessageRef.current) {
      sendMessageRef.current(message);
    } else {
      console.warn('[MPEmbeddedProvider] sendMessage called but not yet initialized');
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    inIframe,
    inWebview,
    isEmbedded,
    isReady,
    setIsReady,
    sendMessage,
    wrapperOrigin,
    setWrapperOrigin,
    embeddedAuthConfirmed,
    setEmbeddedAuthConfirmed
  }), [isEmbedded, isReady, sendMessage, wrapperOrigin, embeddedAuthConfirmed]);
  return <EmbeddedContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="MPEmbeddedProvider" data-sentry-source-file="MPEmbeddedContextProvider.tsx">
      {children}
    </EmbeddedContext.Provider>;
};

// Convenience hook
export const useIsEmbedded = () => {
  const context = useContext(EmbeddedContext);
  // Return default values if provider isn't mounted yet (during dynamic loading)
  if (!context) {
    return {
      isEmbedded: false,
      inIframe: false,
      inWebview: false,
      isReady: false,
      embeddedAuthConfirmed: false
    };
  }
  return context;
};
export default MPEmbeddedProvider;